import { fetchCustomerCID } from '~/utils/customer'
import { VSF_MHJ_BRAZE_CID } from '~/config/cookie'

const PENDING_LOGGED_IN_CUSTOMER_ID_KEY = 'braze_pending_logged_in_customer_id'
let pendingLoggedInCustomerIdFallback: string | null = null

/**
 * Braze identity association.
 *
 * `changeUser(cid)` used to run once, fire-and-forget, in login(). Fresh signups have no CID yet, so
 * the retry got torn down by the post-signup redirect (iOS Safari especially) and the session stayed
 * anonymous, leaving audience-gated banners showing.
 *
 * Instead we persist the CID per-customer (cookie) and re-affirm identity on every authenticated load
 * (via useLogin.fetchUser, run by the global auth middleware). An interrupted attempt self-heals on
 * the next navigation; association is idempotent to avoid redundant token spend.
 */
export function useBrazeIdentity() {
  // Keyed by customerId so concurrent navigations dedup but a different customer (logout -> login)
  // starts immediately. Holding a Promise in useState is safe because ensureIdentity() bails on the
  // server, so it is never serialized. Mirrors useRefreshTokens' `_refresh_in_progress`.
  const inFlight = useState<{ customerId: string; promise: Promise<boolean> } | null>(
    '_braze_identity_in_flight',
    () => null,
  )

  // Separate guard so concurrent ensureIdentityAfterRegistration() calls dedup independently of the
  // login-time one above.
  const postRegistrationInFlight = useState<{ customerId: string; promise: Promise<boolean> } | null>(
    '_braze_identity_post_registration_in_flight',
    () => null,
  )

  const cidCookie = () =>
    useCookie<string | null>(VSF_MHJ_BRAZE_CID, {
      // Braze external id (not a secret): client-readable so the SDK can use it. ~90d matches the
      // registered refresh-token window.
      secure: true,
      sameSite: 'lax',
      path: '/',
      maxAge: 60 * 60 * 24 * 90,
    })

  // Stored as `${customerId}::${cid}` so a different customer can never reuse a stale CID; a key
  // mismatch reads as "not persisted" and forces a fresh fetch.
  function readPersistedCid(customerId: string): string | null {
    const raw = cidCookie().value
    if (!raw) return null
    const separator = raw.indexOf('::')
    if (separator === -1) return null
    const storedCustomerId = raw.slice(0, separator)
    const cid = raw.slice(separator + 2)
    return storedCustomerId === customerId && cid ? cid : null
  }

  function persistCid(customerId: string, cid: string): void {
    cidCookie().value = `${customerId}::${cid}`
  }

  function recordLoggedInIntent(customerId: string): void {
    if (!import.meta.client) return
    pendingLoggedInCustomerIdFallback = customerId
    try {
      sessionStorage.setItem(PENDING_LOGGED_IN_CUSTOMER_ID_KEY, customerId)
    } catch {
      // The in-memory copy keeps this page reliable when Safari or browser policy blocks storage.
    }
  }

  function flushLoggedInIntent(customerId: string): void {
    if (!import.meta.client) return
    let pendingCustomerId = pendingLoggedInCustomerIdFallback
    try {
      pendingCustomerId = pendingLoggedInCustomerIdFallback ?? sessionStorage.getItem(PENDING_LOGGED_IN_CUSTOMER_ID_KEY)
    } catch {
      // Fall back to page memory when storage is unavailable.
    }
    if (pendingCustomerId !== customerId) return

    // Consume before emitting so later identity checks cannot duplicate this login.
    pendingLoggedInCustomerIdFallback = null
    try {
      sessionStorage.removeItem(PENDING_LOGGED_IN_CUSTOMER_ID_KEY)
    } catch {
      // Storage may remain unavailable; the in-memory intent is still consumed.
    }
    useBrazeTracking().logCustomEvent('logged_in', undefined, { isLoggedIn: true })
  }

  function clearPersistedCid(): void {
    cidCookie().value = null
    inFlight.value = null
    postRegistrationInFlight.value = null
    if (import.meta.client) {
      pendingLoggedInCustomerIdFallback = null
      try {
        sessionStorage.removeItem(PENDING_LOGGED_IN_CUSTOMER_ID_KEY)
      } catch {
        // Storage can be unavailable in Safari private mode; logout must still complete.
      }
    }
  }

  // Registered customerId of the current access token, or null for none/guest. Detects a logout or
  // account switch that landed while a CID lookup was in flight. Decodes quietly — isGuestToken /
  // getIdsFromToken decode twice and log on hiccups.
  function currentRegisteredCustomerId(): string | null {
    const token = useCookie<string | null>('access_token').value
    if (!token) return null
    try {
      const parts = decodeToken(token).payload.isb?.split('::') ?? []
      const upn = parts
        .find((part) => part.startsWith('upn:'))
        ?.split(':')[1]
        ?.toLowerCase()
      if (!upn || upn === 'guest') return null
      // rcid only: the guard must confirm a *registered* identity, never fall back to a guest id.
      return parts.find((part) => part.startsWith('rcid:'))?.split(':')[1] || null
    } catch {
      return null
    }
  }

  function isIdentityConfirmed(): boolean {
    if (!import.meta.client) return false
    const customerId = currentRegisteredCustomerId()
    if (!customerId) return false
    const cid = readPersistedCid(customerId)
    return Boolean(cid && useNuxtApp().$braze.sdk.getUser()?.getUserId() === cid)
  }

  async function resolveIdentity(customerId: string): Promise<boolean> {
    const { $braze } = useNuxtApp()

    // Reuse the persisted CID; only hit the network when we don't have one yet.
    let cid = readPersistedCid(customerId)
    let persistedNow = false
    if (!cid) {
      // Fresh signups have no CID yet, so each full reload starts a new ~21-30s fetchCustomerCID retry
      // ladder (the in-flight guard dedups within a page, not across reloads). Bounded per customer;
      // add a short-lived "recently attempted" throttle if /customer/cid load ever shows.
      try {
        const res = await fetchCustomerCID()
        cid = res?.cid ?? null
        // The fetch resolves against live cookies, not our argument: if the shopper logged out or
        // switched during the retry window, discard the CID — their own ensureIdentity() wins the race.
        if (cid && currentRegisteredCustomerId() !== customerId) return false
        if (cid) {
          persistCid(customerId, cid)
          persistedNow = true
        }
      } catch (err) {
        // CID not provisioned yet (common just after signup); the next navigation re-attempts.
        console.error('Braze identity: failed to fetch customer CID', err)
        return false
      }
    }
    if (!cid) return false

    // Re-check before mutating (covers the no-await persisted path), and before the idempotency check
    // so a stale caller can't short-circuit as "already associated" after a switch.
    if (currentRegisteredCustomerId() !== customerId) {
      // Undo only a mapping THIS call just wrote; never wipe a pre-existing valid CID.
      if (persistedNow && readPersistedCid(customerId) === cid) cidCookie().value = null
      return false
    }

    // getUserId() can be null mid-restore — associating again is fine.
    if ($braze.sdk.getUser()?.getUserId() === cid) {
      useBrazeTracking().syncLoggedInStatus(true)
      flushLoggedInIntent(customerId)
      return true
    }

    $braze.sdk.changeUser(cid)
    useBrazeTracking().syncLoggedInStatus(true)
    flushLoggedInIntent(customerId)
    $braze.debugLogger(`Braze identity associated for ${cid}. Braze Device ID: ${$braze.sdk.getDeviceId()}`)
    return true
  }

  /**
   * Ensure Braze is identified as `customerId`. Safe on every authenticated navigation: same-customer
   * calls share one in-flight promise and the CID fetch happens at most once (then served from cookie).
   * Client-only and non-throwing — callers can fire-and-forget. Resolves `true` when the SDK is (or was
   * just) associated with the intended CID.
   */
  function ensureIdentity(customerId: string | null | undefined): Promise<boolean> {
    if (import.meta.server || !customerId) return Promise.resolve(false)
    if (inFlight.value?.customerId === customerId) return inFlight.value.promise

    const promise: Promise<boolean> = resolveIdentity(customerId).finally(() => {
      // Only clear if this exact promise still owns the guard: matching on customerId alone would let a
      // stale settle wipe the newer guard installed by a clear + same-customer restart.
      if (inFlight.value?.promise === promise) inFlight.value = null
    })
    inFlight.value = { customerId, promise }
    return promise
  }

  /**
   * Ensure Braze is identified as `customerId` once registration completes, i.e. after
   * createCustomerAddress triggers the downstream customer publish. The login-time attempt is usually
   * mid-sleep in its 3s/6s/12s backoff by now, so awaiting it would leave the signup banner stale until
   * its next tick — hence an immediate attempt instead. resolveIdentity() reads the persisted CID first
   * and short-circuits on getUserId(), so a login-time ladder that already succeeded costs no extra
   * fetch. Client-only, non-throwing, deduplicated — callers can fire-and-forget.
   */
  function ensureIdentityAfterRegistration(customerId: string | null | undefined): Promise<boolean> {
    if (import.meta.server || !customerId) return Promise.resolve(false)
    if (postRegistrationInFlight.value?.customerId === customerId) return postRegistrationInFlight.value.promise

    // Call resolveIdentity() directly so this runs even while the pre-publish ladder is still sleeping,
    // and take over `inFlight` so navigation reuses this attempt rather than starting another lookup.
    const promise: Promise<boolean> = resolveIdentity(customerId).finally(() => {
      if (postRegistrationInFlight.value?.promise === promise) postRegistrationInFlight.value = null
      if (inFlight.value?.promise === promise) inFlight.value = null
    })
    postRegistrationInFlight.value = { customerId, promise }
    inFlight.value = { customerId, promise }
    return promise
  }

  return {
    ensureIdentity,
    ensureIdentityAfterRegistration,
    recordLoggedInIntent,
    clearPersistedCid,
    isIdentityConfirmed,
  }
}
